import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const PregnancyFH = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-heart-pulse-line text-[#f04e58]"></i>
              Women's Cardiovascular Health Series
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Pregnancy + Familial Hypercholesterolemia (FH)
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Understanding cardiovascular considerations, medication safety, and genetic transmission during pregnancy for women with Familial Hypercholesterolemia.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          {/* Introduction */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Familial Hypercholesterolemia (FH) in Pregnancy: What Every Woman Should Know
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4 text-lg">
              Pregnancy is a time of careful planning, monitoring, and protection — not just for the baby, but for the mother's long-term health.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4 text-lg">
              For women living with Familial Hypercholesterolemia (FH), pregnancy requires special cardiovascular consideration.
            </p>
            <p className="text-gray-700 leading-relaxed text-lg">
              FH is an inherited condition that causes lifelong elevation of LDL ("bad") cholesterol. While many women with FH have healthy pregnancies, understanding the unique risks and management strategies is critical for protecting both maternal heart health and the next generation.
            </p>
          </div>

          {/* Why FH Matters During Pregnancy */}
          <div className="mb-16 bg-[#fef9f9] rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Why FH Matters During Pregnancy
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Pregnancy naturally increases cholesterol levels.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Even in women without FH:
            </p>
            <div className="space-y-2 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">LDL and triglycerides rise</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">Total cholesterol increases</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">Lipids peak in the third trimester</p>
              </div>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              For women with FH — who already start with elevated LDL — this physiologic rise can lead to extremely high cholesterol levels during pregnancy.
            </p>
            <div className="bg-white rounded-xl p-6 border-l-4 border-[#f04e58]">
              <p className="text-gray-900 font-semibold mb-2">The key questions become:</p>
              <div className="space-y-2">
                <p className="text-gray-700">• Is it safe for mother and baby?</p>
                <p className="text-gray-700">• What happens to cholesterol medications?</p>
                <p className="text-gray-700">• What are the long-term risks?</p>
              </div>
            </div>
          </div>

          {/* Complications Grid */}
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              Key Considerations for Women with FH During Pregnancy
            </h3>
            <div className="grid gap-6">
              {/* Card 1: Cardiovascular Risk */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 flex-shrink-0">
                    <i className="ri-heart-pulse-line text-2xl text-[#f04e58]"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f04e58] text-white font-bold text-sm flex-shrink-0">
                        1
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">
                        Cardiovascular Risk in Pregnant Women with FH
                      </h4>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Most young women with Heterozygous Familial Hypercholesterolemia (HeFH) who do not yet have established heart disease tolerate pregnancy well.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  However, risks increase in women who:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Have existing coronary artery disease</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Have had prior heart attacks</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Have severe LDL elevations</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Have Homozygous Familial Hypercholesterolemia (HoFH)</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  In these cases, pregnancy may:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Increase cardiac workload</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Exacerbate ischemia</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Require multidisciplinary cardiac care</p>
                  </div>
                </div>
                <div className="bg-[#0d9488]/10 rounded-xl p-4">
                  <p className="text-gray-900 font-semibold">
                    Pre-pregnancy cardiovascular evaluation is strongly recommended.
                  </p>
                </div>
              </div>

              {/* Card 2: Statin Therapy */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 flex-shrink-0">
                    <i className="ri-medicine-bottle-line text-2xl text-[#0d9488]"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0d9488] text-white font-bold text-sm flex-shrink-0">
                        2
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">
                        Statin Therapy and Pregnancy
                      </h4>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  One of the most common concerns is medication safety.
                </p>
                <p className="text-gray-900 font-semibold mb-2">Statins:</p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Historically, statins were contraindicated during pregnancy due to concerns about fetal development.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Recent regulatory updates have softened strict contraindications in certain high-risk women, but in most cases:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Statins are discontinued prior to conception</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Therapy is paused during pregnancy</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Restarted after delivery (if not breastfeeding)</p>
                  </div>
                </div>
                <div className="bg-[#f04e58]/10 rounded-xl p-4">
                  <p className="text-gray-900 font-semibold">
                    The decision must be individualized based on maternal cardiovascular risk.
                  </p>
                </div>
              </div>

              {/* Card 3: Other Therapies */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 flex-shrink-0">
                    <i className="ri-capsule-line text-2xl text-[#f04e58]"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f04e58] text-white font-bold text-sm flex-shrink-0">
                        3
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">
                        Other Lipid-Lowering Therapies
                      </h4>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900 font-semibold mb-1">Ezetimibe:</p>
                    <p className="text-gray-700 text-sm">Typically discontinued during pregnancy</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900 font-semibold mb-1">PCSK9 inhibitors:</p>
                    <p className="text-gray-700 text-sm">Insufficient safety data; usually stopped</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900 font-semibold mb-1">Bempedoic acid / Inclisiran:</p>
                    <p className="text-gray-700 text-sm">Limited pregnancy data</p>
                  </div>
                  <div className="bg-[#0d9488]/10 rounded-lg p-4">
                    <p className="text-gray-900 font-semibold mb-1">LDL apheresis:</p>
                    <p className="text-gray-700 text-sm">May be continued in severe HoFH cases</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mt-4">
                  In very high-risk women — especially those with HoFH — LDL apheresis has been used safely during pregnancy to control extreme cholesterol levels.
                </p>
              </div>

              {/* Card 4: Natural Cholesterol Changes */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 flex-shrink-0">
                    <i className="ri-line-chart-line text-2xl text-[#0d9488]"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0d9488] text-white font-bold text-sm flex-shrink-0">
                        4
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">
                        Natural Cholesterol Changes During Pregnancy
                      </h4>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  It's important to understand:
                </p>
                <div className="bg-[#0d9488]/10 rounded-xl p-4 mb-4">
                  <p className="text-gray-900 font-semibold">
                    Elevated cholesterol during pregnancy is physiologic and supports fetal development.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  However, in FH:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">LDL may reach very high levels</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Monitoring becomes important</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Temporary medication pause does not erase long-term management gains</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  Short-term LDL elevation during pregnancy is generally acceptable in low-risk women with no established heart disease.
                </p>
              </div>

              {/* Card 5: Genetic Transmission */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 flex-shrink-0">
                    <i className="ri-dna-line text-2xl text-[#f04e58]"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f04e58] text-white font-bold text-sm flex-shrink-0">
                        5
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">
                        Genetic Transmission to the Baby
                      </h4>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  FH is inherited in an autosomal dominant pattern.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  This means:
                </p>
                <div className="space-y-3 mb-4">
                  <div className="bg-[#f04e58]/10 rounded-lg p-4">
                    <p className="text-gray-900 font-semibold mb-1">50% chance</p>
                    <p className="text-gray-700 text-sm">
                      Each child has a 50% chance of inheriting FH (if one parent has HeFH)
                    </p>
                  </div>
                  <div className="bg-[#f04e58]/10 rounded-lg p-4">
                    <p className="text-gray-900 font-semibold mb-1">Higher risk</p>
                    <p className="text-gray-700 text-sm">
                      If both parents carry FH mutations, risk increases significantly
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Early awareness allows:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Pediatric screening</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Early intervention</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Prevention of premature heart disease in the next generation</p>
                  </div>
                </div>
                <div className="bg-[#0d9488]/10 rounded-xl p-4">
                  <p className="text-gray-900 font-semibold">
                    Cascade family screening is one of the most powerful benefits of FH diagnosis.
                  </p>
                </div>
              </div>

              {/* Card 6: Breastfeeding */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 flex-shrink-0">
                    <i className="ri-parent-line text-2xl text-[#0d9488]"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0d9488] text-white font-bold text-sm flex-shrink-0">
                        6
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">
                        Breastfeeding Considerations
                      </h4>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Statins are generally not recommended during breastfeeding.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Women must balance:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Cardiovascular risk</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Duration of breastfeeding</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Timing of restarting therapy</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  In high-risk women, earlier medication resumption may be prioritized.
                </p>
                <div className="bg-[#f04e58]/10 rounded-xl p-4">
                  <p className="text-gray-900 font-semibold">
                    These decisions require shared decision-making between cardiology and obstetric teams.
                  </p>
                </div>
              </div>

              {/* Card 7: Cardiovascular Stress Test */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 flex-shrink-0">
                    <i className="ri-heart-pulse-line text-2xl text-[#f04e58]"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f04e58] text-white font-bold text-sm flex-shrink-0">
                        7
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">
                        Pregnancy as a Cardiovascular "Stress Test"
                      </h4>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Pregnancy increases:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Blood volume</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Cardiac output</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Metabolic demand</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For women with underlying coronary artery disease due to FH, this can unmask symptoms such as:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Chest pain</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Shortness of breath</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Palpitations</p>
                  </div>
                </div>
                <div className="bg-[#f04e58]/10 rounded-xl p-4">
                  <p className="text-gray-900 font-semibold">
                    Pregnancy can sometimes reveal previously silent cardiovascular disease.
                  </p>
                </div>
              </div>

              {/* Card 8: Preconception Planning */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 flex-shrink-0">
                    <i className="ri-calendar-check-line text-2xl text-[#0d9488]"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0d9488] text-white font-bold text-sm flex-shrink-0">
                        8
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">
                        Preconception Planning Is Critical
                      </h4>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Women with FH should ideally:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Undergo lipid evaluation</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Assess cardiovascular status</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Review medication safety</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Develop a pregnancy management plan</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  High-risk patients may require:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Cardiology consultation</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Stress testing</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Imaging studies</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-[#0d9488] to-[#10b981] rounded-xl p-4 text-white">
                  <p className="font-semibold">
                    Preparation significantly reduces complications.
                  </p>
                </div>
              </div>

              {/* Card 9: HoFH and High-Risk Pregnancy */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#f04e58]/10 flex-shrink-0">
                    <i className="ri-alert-line text-2xl text-[#f04e58]"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#f04e58] text-white font-bold text-sm flex-shrink-0">
                        9
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">
                        HoFH and High-Risk Pregnancy
                      </h4>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Women with Homozygous FH (HoFH) face more complex considerations:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Extremely high LDL levels</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Early vascular disease</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Aortic involvement possible</p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  In these cases:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Multidisciplinary care is essential</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">LDL apheresis may be continued</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Close monitoring throughout pregnancy is required</p>
                  </div>
                </div>
                <div className="bg-[#0d9488]/10 rounded-xl p-4">
                  <p className="text-gray-900 font-semibold">
                    Despite higher risk, successful pregnancies have been reported with appropriate management.
                  </p>
                </div>
              </div>

              {/* Card 10: Long-Term Maternal Outcomes */}
              <div className="bg-white rounded-2xl border border-gray-200 p-8 hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#0d9488]/10 flex-shrink-0">
                    <i className="ri-time-line text-2xl text-[#0d9488]"></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0d9488] text-white font-bold text-sm flex-shrink-0">
                        10
                      </div>
                      <h4 className="text-xl font-bold text-gray-900">
                        Long-Term Maternal Outcomes
                      </h4>
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  One pregnancy without statins does not erase long-term treatment benefit.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  However:
                </p>
                <div className="space-y-2 mb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Repeated pregnancies with long medication interruptions may increase lifetime LDL exposure</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Postpartum lipid management should resume promptly</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                    <p className="text-gray-700">Ongoing cardiovascular follow-up is important</p>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-[#0d9488] to-[#10b981] rounded-xl p-4 text-white">
                  <p className="font-semibold">
                    The goal is lifelong LDL control — not just pregnancy-specific management.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Raising Awareness Section */}
          <div className="mb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8] rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Raising Awareness
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Many women discover FH only after:
            </p>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">A family member has an early heart attack</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">Cholesterol screening during pregnancy</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-[#f04e58] mt-2 flex-shrink-0"></div>
                <p className="text-gray-700">Unexpected cardiac symptoms</p>
              </div>
            </div>
            <div className="bg-white rounded-xl p-6 border-l-4 border-[#0d9488] mb-6">
              <p className="text-gray-900 font-semibold mb-2">
                Because FH affects approximately 1 in 250 individuals, awareness among women of childbearing age is essential.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Early diagnosis allows:
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0d9488]/10 mx-auto mb-3">
                  <i className="ri-calendar-check-line text-2xl text-[#0d9488]"></i>
                </div>
                <p className="text-gray-900 font-semibold">Safe pregnancy planning</p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0d9488]/10 mx-auto mb-3">
                  <i className="ri-medicine-bottle-line text-2xl text-[#0d9488]"></i>
                </div>
                <p className="text-gray-900 font-semibold">Informed medication decisions</p>
              </div>
              <div className="bg-white rounded-xl p-6 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#0d9488]/10 mx-auto mb-3">
                  <i className="ri-shield-check-line text-2xl text-[#0d9488]"></i>
                </div>
                <p className="text-gray-900 font-semibold">Protection of future generations</p>
              </div>
            </div>
          </div>

          {/* Final Takeaway Section */}
          <div className="mb-16 bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-10 text-white">
            <h3 className="text-3xl font-bold mb-6">Final Takeaway</h3>
            <p className="text-white/90 leading-relaxed text-lg mb-6">
              Familial Hypercholesterolemia does not prevent a healthy pregnancy.
            </p>
            <p className="text-white/90 leading-relaxed text-lg mb-6">
              But it does require awareness, planning, and coordinated care.
            </p>
            <div className="bg-white/10 rounded-xl p-6 mb-6">
              <p className="text-white font-semibold mb-4">With proper management:</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                  <p className="text-white/90">Most women with HeFH can have safe pregnancies</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                  <p className="text-white/90">Even women with severe FH can achieve successful outcomes</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#0d9488] mt-2 flex-shrink-0"></div>
                  <p className="text-white/90">Early screening protects both mother and child</p>
                </div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-xl p-6">
              <p className="text-white leading-relaxed mb-4">
                If you have LDL above 190 mg/dL or a family history of early heart disease, consider evaluation for FH — especially before pregnancy.
              </p>
              <p className="text-white font-bold text-lg">
                Because when it comes to FH, knowledge protects generations.
              </p>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-16 bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Planning for Pregnancy with FH?</h3>
            <p className="text-white/90 leading-relaxed mb-6">
              If you have Familial Hypercholesterolemia and are planning to become pregnant, early consultation with a cardiologist and maternal-fetal medicine specialist can help ensure the healthiest outcomes for you and your baby.
            </p>
            <Link
              to="/consultation"
              className="inline-flex items-center px-6 py-3 bg-white text-[#f04e58] rounded-full font-semibold hover:bg-gray-100 transition-all whitespace-nowrap"
            >
              Schedule a Consultation
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PregnancyFH;
